import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Eye, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ResetPassword() {
  const [formData, setFormData] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isFocused, setIsFocused] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const resetToken = location.state?.resetToken;

  useEffect(() => {
    if (!email || !resetToken) {
      navigate('/forgot-password');
    }
  }, [email, resetToken, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.new_password !== formData.confirm_password) {
      setError('Passwords do not match.');
      return;
    }
    if (formData.new_password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { 
        email, 
        reset_token: resetToken,
        new_password: formData.new_password 
      });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="relative min-h-screen w-full flex items-center justify-center p-6 overflow-hidden bg-[#0A0C10]">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/[0.03] backdrop-blur-2xl border border-white/[0.08] p-10 rounded-[2.5rem] text-center max-w-[400px]"
        >
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-green-500/30">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-serif font-bold text-white mb-2">Password Reset!</h2>
          <p className="text-white/40 mb-6">Your password has been updated successfully. Redirecting you to login...</p>
          <div className="w-full bg-white/5 h-1 rounded-full overflow-hidden">
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ duration: 3 }}
              className="h-full bg-green-500"
            />
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center p-6 overflow-hidden bg-[#0A0C10]">
      {/* Background Aura Effects */}
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-[#D0021B]/10 blur-[120px]"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-indigo-600/10 blur-[120px]"></div>
      
      <motion.div 
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="relative z-10 w-full max-w-[480px]"
      >
        <div className="group relative bg-white/[0.03] backdrop-blur-2xl border border-white/[0.08] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.6)] rounded-[2.5rem] overflow-hidden">
          <div className="px-8 pt-12 pb-10">
            <div className="text-center mb-10">
              <h1 className="font-serif text-3xl font-bold text-white tracking-tight mb-2 uppercase">
                New Password
              </h1>
              <p className="text-white/40 text-sm tracking-wide uppercase font-bold">
                Set a secure password for your account
              </p>
            </div>

            <AnimatePresence>
              {error && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-6 overflow-hidden"
                >
                  <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-200 text-sm flex items-center gap-3">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="group relative">
                <label className={`absolute left-10 transition-all duration-300 pointer-events-none ${
                    isFocused === 'new_password' || formData.new_password ? '-top-3 text-[10px] text-white font-bold px-2 py-0.5 bg-[#D0021B] rounded-md z-10 scale-100 shadow-lg' : 'top-4 text-sm text-white/40'
                  }`}
                >
                  NEW PASSWORD
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className={`w-5 h-5 transition-colors duration-300 ${isFocused === 'new_password' ? 'text-[#D0021B]' : 'text-white/20'}`} />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="new_password"
                    value={formData.new_password}
                    onFocus={() => setIsFocused('new_password')}
                    onBlur={() => setIsFocused('')}
                    onChange={handleChange}
                    className="w-full pl-12 pr-12 py-4 bg-white/[0.03] border border-white/10 focus:border-[#D0021B]/40 rounded-2xl text-white text-sm focus:bg-white/[0.05] focus:ring-4 focus:ring-[#D0021B]/5 transition-all outline-none"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/20 hover:text-white/60 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="group relative">
                <label className={`absolute left-10 transition-all duration-300 pointer-events-none ${
                    isFocused === 'confirm_password' || formData.confirm_password ? '-top-3 text-[10px] text-white font-bold px-2 py-0.5 bg-[#D0021B] rounded-md z-10 scale-100 shadow-lg' : 'top-4 text-sm text-white/40'
                  }`}
                >
                  CONFIRM PASSWORD
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className={`w-5 h-5 transition-colors duration-300 ${isFocused === 'confirm_password' ? 'text-[#D0021B]' : 'text-white/20'}`} />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="confirm_password"
                    value={formData.confirm_password}
                    onFocus={() => setIsFocused('confirm_password')}
                    onBlur={() => setIsFocused('')}
                    onChange={handleChange}
                    className="w-full pl-12 pr-4 py-4 bg-white/[0.03] border border-white/10 focus:border-[#D0021B]/40 rounded-2xl text-white text-sm focus:bg-white/[0.05] focus:ring-4 focus:ring-[#D0021B]/5 transition-all outline-none"
                    required
                  />
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.01, y: -2 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className="relative w-full py-4 bg-green-600 text-white font-bold rounded-2xl shadow-[0_8px_32px_rgba(22,163,74,0.3)] hover:shadow-[0_12px_48px_rgba(22,163,74,0.45)] transition-all disabled:opacity-50 group overflow-hidden mt-4"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                {loading ? 'RESETTING PASSWORD...' : 'RESET PASSWORD'}
              </motion.button>
            </form>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
